import { supabase } from "@/integrations/supabase/client";
import { ImageUploadProvider, VariantImageMetadata, ImageUploadResult } from "@/types/variantImage";

export class SupabaseImageUploadProvider implements ImageUploadProvider {
  private bucketName = "product-images";

  async upload(file: File, metadata: VariantImageMetadata): Promise<ImageUploadResult> {
    try {
      // Sanitize all parts of the path to remove invalid characters
      const sanitize = (str: string) => str.replace(/[^a-zA-Z0-9.-]/g, '_');
      
      const timestamp = Date.now();
      const sanitizedFileName = sanitize(file.name);
      const sanitizedSize = sanitize(metadata.potSize);
      const sanitizedType = sanitize(metadata.potType);
      const sanitizedColor = sanitize(metadata.potColor);
      
      const fileName = `variants/${metadata.productId}/${sanitizedSize}-${sanitizedType}-${sanitizedColor}/${timestamp}-${sanitizedFileName}`;

      // Upload to Supabase Storage
      const { data, error } = await supabase.storage
        .from(this.bucketName)
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (error) {
        throw new Error(`Upload failed: ${error.message}`);
      }

      const imageUrl = this.getPublicUrl(data.path);

      return {
        success: true,
        imageUrl
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Upload failed'
      };
    }
  }

  async delete(imagePath: string): Promise<void> {
    // Extract path from full URL if needed
    const path = imagePath.includes('supabase.co') 
      ? imagePath.split('/').slice(-4).join('/') // Get last 4 parts of path
      : imagePath;

    const { error } = await supabase.storage
      .from(this.bucketName)
      .remove([path]);

    if (error) {
      throw new Error(`Delete failed: ${error.message}`);
    }
  }

  getPublicUrl(path: string): string {
    const { data } = supabase.storage
      .from(this.bucketName)
      .getPublicUrl(path);

    return data.publicUrl;
  }
}

// Factory function to get the current provider
export const getImageUploadProvider = (): ImageUploadProvider => {
  return new SupabaseImageUploadProvider();
};